import React, { useEffect, useState, useContext } from 'react';
import { InquiryInput } from 'sr-types/lib/shortlist/v1/graphql';
import { FormProvider } from '../../../common/form/FormContext';
import { getDateRangeListeners } from '../../../supply/booking/ScheduleCalculator';
import OrganizationInviteFormModal from '../OrganizationInviteFormModal';
import PersonInviteFormModal from '../PersonInviteFormModal';
import { checkForEmail, InquiryPropsType, OrganizationInviteFormData, PEOPLE_TYPE } from './inquiry';
import { useSendInquiry } from './inquiryApi';
import { useLazyQuery } from '@apollo/client';
import { inquiryPreviewQuery, client } from '../../shortlist';
import { UserContext } from '../../../common/auth/UserContext';
import { useStepper } from '../../../common/components/StepperContext';
import StepPersonRequestAvails from '../../../hiringManager/StepPersonRequestAvails';
import { FETCH_POLICY_CONFIG } from '../../../common/constants/constants';
import StepSupplierRequestAvails from '../../../hiringManager/StepSupplierRequestAvails';
import { AttachmentInput } from 'sr-types/lib/resource/v1/graphql';
import { Attachment } from 'sr-types/lib/production/v1/graphql';
import associateTag from '../../../common/utils/associateTag';
import { useBrowserOrAccountLocale } from '../../../common/hooks/useBrowserOrAccountLocale';

export default ({
    type,
    entityDetails,
    isEntityLoading,
    validationRules,
    isModalOpen,
    onCloseModal,
    data,
    onSendInquirySuccess,
    toInquiryInput,
    isInStepper = false,
    isRequestAvailsForUser = false,
    filters = undefined,
    expandMarketplace,
    expandedContainer,
    showCandidateListOnly = false,
}: InquiryPropsType) => {
    const { sendInquiryInvitation, isInquirySending } = useSendInquiry();

    let getStepData: (step: number) => any;
    let activeStep: number | undefined;

    if (isInStepper && useStepper) {
        getStepData = useStepper().getStepData;
        activeStep = useStepper().activeStep;
    }

    const [state, setState] = useState({ ...data });
    const [validations, setValidations] = useState({ ...validationRules });
    const [itemsWithNoEmail, setItemsWithNoEmail] = useState([]);
    const [isEmailModalOpen, setIsEmailModalOpen] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isPreview, setIsPreview] = useState(false);
    const [isAttachmentsInUploadMode, setIsAttachmentsInUploadMode] = useState(false);
    const locale = useBrowserOrAccountLocale();
    const { activeOrganizationAccount } = useContext(UserContext);
    const [getInquiryPreviewData, { loading: isPreviewLoading, error, data: previewData }] = useLazyQuery(
        inquiryPreviewQuery,
        {
            client,
            context: {
                headers: {
                    ownerId: activeOrganizationAccount
                }
            },
            fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy
        }
    );

    useEffect(() => {
        if (getStepData && activeStep !== undefined) {
            const stepVal = getStepData(activeStep);
            if (stepVal) {
                setState({ ...stepVal });
            }
        }
    }, []);

    const onDeleteAttachmentFromState = (attachments: AttachmentInput[], input: AttachmentInput) => {
        const findInState = state?.attachments?.find(
            (attachment: AttachmentInput) => attachment.entityReference.id === input.entityReference.id
        );
        let filteredAttachments: AttachmentInput[] = [];
        if (findInState) {
            filteredAttachments = attachments.filter(
                (attachment: AttachmentInput) => attachment.entityReference.id !== input.entityReference.id
            );
            setState({
                ...state,
                attachments: filteredAttachments
            });
            return filteredAttachments;
        }
    };

    const saveAttachmentTags = (inputs: Attachment[]) => {
        inputs.forEach((a) => {
            if (a.tags) {
                associateTag(activeOrganizationAccount, a.entityReference.id, a.tags);
            }
        });
        setIsAttachmentsInUploadMode(false);
    };

    useEffect(() => {
        if (entityDetails) {
            const { items } = entityDetails;
            checkForEmail(items, type, state, setValidations, setIsEmailModalOpen, setItemsWithNoEmail);
        }
    }, [entityDetails, state?.[type]]);

    const sendInvitation = () => {
        const inquiryInput: InquiryInput[] = toInquiryInput(state);

        sendInquiryInvitation(inquiryInput).then(() => onSendInquirySuccess(inquiryInput));
    };

    const onClickContinue = () => {
        setIsSaving(true);
        setValidations({ ...validationRules });
        setTimeout(() => {
            setIsEmailModalOpen(false);
            setIsSaving(false);
        }, 150);
    };

    return (
        <FormProvider
            state={state}
            setState={setState}
            validationRules={validations}
            listeners={getDateRangeListeners('dateRange', locale)}
        >
            {type === PEOPLE_TYPE ? (
                isInStepper ? (
                    <StepPersonRequestAvails
                        productionName={data.production?.name}
                        productionOptions={data?.productionOptions}
                        productionDateRange={data?.dateRange}
                        isRequestAvailsForUser={isRequestAvailsForUser}
                        filters={filters}
                        expandMarketplace={expandMarketplace}
                        expandedContainer={expandedContainer}
                        saveAttachmentTags={saveAttachmentTags}
                        onDeleteAttachmentFromState={onDeleteAttachmentFromState}
                        setIsInUploadMode={setIsAttachmentsInUploadMode}
                        isAttachmentsInUploadMode={isAttachmentsInUploadMode}
                        showCandidateListOnly={showCandidateListOnly}
                    />
                ) : (
                    <PersonInviteFormModal
                        isPreview={isPreview}
                        setIsPreview={setIsPreview}
                        isOpen={isModalOpen}
                        onClose={onCloseModal}
                        sendInvitation={sendInvitation}
                        isSaving={isSaving || isInquirySending}
                        productionName={data.production?.name}
                        productionDateRange={data?.dateRange}
                        productionOptions={data?.productionOptions}
                        isLoading={isEntityLoading}
                        isEmailModalOpen={isEmailModalOpen}
                        peopleWithNoContact={itemsWithNoEmail}
                        onClickContinue={onClickContinue}
                        entityDetails={entityDetails}
                        getInquiryPreviewData={getInquiryPreviewData}
                        isPreviewLoading={isPreviewLoading}
                        previewData={previewData?.inquiryPreview}
                        saveAttachmentTags={saveAttachmentTags}
                        onDeleteAttachmentFromState={onDeleteAttachmentFromState}
                        setIsInUploadMode={setIsAttachmentsInUploadMode}
                        isAttachmentsInUploadMode={isAttachmentsInUploadMode}
                    />
                )
            ) : isInStepper ? (
                <StepSupplierRequestAvails
                    expandMarketplace={expandMarketplace}
                    expandedContainer={expandedContainer}
                    data={data as OrganizationInviteFormData}
                    isRequestAvailsForUser={isRequestAvailsForUser}
                    filters={filters}
                    saveAttachmentTags={saveAttachmentTags}
                    onDeleteAttachmentFromState={onDeleteAttachmentFromState}
                    setIsInUploadMode={setIsAttachmentsInUploadMode}
                    isAttachmentsInUploadMode={isAttachmentsInUploadMode}
                />
            ) : (
                <OrganizationInviteFormModal
                    isPreview={isPreview}
                    setIsPreview={setIsPreview}
                    isOpen={isModalOpen}
                    onClose={onCloseModal}
                    sendInvitation={sendInvitation}
                    isLoading={isEntityLoading}
                    isSaving={isSaving || isInquirySending}
                    isEmailModalOpen={isEmailModalOpen}
                    itemsWithNoEmail={itemsWithNoEmail}
                    data={data as OrganizationInviteFormData}
                    entityDetails={entityDetails}
                    onClickContinue={onClickContinue}
                    getInquiryPreviewData={getInquiryPreviewData}
                    isPreviewLoading={isPreviewLoading}
                    previewData={previewData?.inquiryPreview}
                    saveAttachmentTags={saveAttachmentTags}
                    onDeleteAttachmentFromState={onDeleteAttachmentFromState}
                    setIsInUploadMode={setIsAttachmentsInUploadMode}
                    isAttachmentsInUploadMode={isAttachmentsInUploadMode}
                />
            )}
        </FormProvider>
    );
};
